import { useCallback, useEffect, useRef, useState } from 'react'
import { contentTypeOf, isPhotoOrVideo, MAX_FILE_BYTES, MAX_FILES, runPool } from '../../bulkMedia/stage'
import type { JobDetail } from '../../bulkMedia/types'
import type { VaultUploadApi } from '../LibraryDeps'

/**
 * The Vault page's uploads (ADR 0025): each picked file is sent to the console,
 * then one job per batch puts them into this Account's Vault (and the open
 * Category). A placeholder tile follows each file until OnlyFans has it.
 */

export type UploadStep =
  | 'queued' // waiting for its turn to go to the console
  | 'staging' // going to the console
  | 'pending' // job started, not uploaded to OnlyFans yet
  | 'converted' // on OnlyFans, being processed
  | 'in_vault' // in the Vault, being added to the Category
  | 'waiting' // OnlyFans' limit: the job tries again later
  | 'done'
  | 'failed'

export interface VaultUpload {
  key: string
  name: string
  kind: 'photo' | 'video'
  preview?: string
  /** Where it goes: a Category id, or the Vault's All media. */
  target: string
  targetName: string | null
  step: UploadStep
  progress: number
  file: File
  fileId?: string
  jobId?: string
  error?: string
}

const PARALLEL_FILES = 2
const POLL_MS = 3000

let seq = 0

export function useVaultUploads(api: VaultUploadApi | undefined, onLanded: (keys: string[]) => Promise<void> | void) {
  const [uploads, setUploads] = useState<VaultUpload[]>([])
  const live = useRef<VaultUpload[]>([])
  live.current = uploads
  const landed = useRef(onLanded)
  landed.current = onLanded
  const aborts = useRef(new Set<AbortController>())

  const patch = useCallback((keys: string[], change: Partial<VaultUpload> | ((u: VaultUpload) => Partial<VaultUpload>)) => {
    setUploads((list) => list.map((u) => (keys.includes(u.key) ? { ...u, ...(typeof change === 'function' ? change(u) : change) } : u)))
  }, [])

  /** Stage these uploads, then start one job for the ones that made it. */
  const run = useCallback(
    async (batch: VaultUpload[]) => {
      if (!api) return
      const staged: { key: string; fileId: string }[] = []
      await runPool(batch, PARALLEL_FILES, async (u) => {
        const ctrl = new AbortController()
        aborts.current.add(ctrl)
        patch([u.key], { step: 'staging', progress: 0, error: undefined })
        try {
          const fileId = await api.stage(u.file, (progress) => patch([u.key], { progress }), ctrl.signal)
          staged.push({ key: u.key, fileId })
          patch([u.key], { fileId, progress: 1 })
        } catch (e) {
          if (!ctrl.signal.aborted) patch([u.key], { step: 'failed', error: e instanceof Error ? e.message : String(e) })
        } finally {
          aborts.current.delete(ctrl)
        }
      })
      if (!staged.length) return
      const keys = staged.map((s) => s.key)
      const target = batch[0]?.targetName ?? null
      try {
        const job = await api.start(
          staged.map((s) => s.fileId),
          target,
        )
        patch(keys, { step: 'pending', jobId: job.id })
      } catch (e) {
        patch(keys, { step: 'failed', error: e instanceof Error ? e.message : String(e) })
      }
    },
    [api, patch],
  )

  /** Pick files for the open Category (null name = All media). Returns the names refused. */
  const add = useCallback(
    (files: File[], target: string, targetName: string | null) => {
      const refused: string[] = []
      const batch: VaultUpload[] = []
      for (const file of files) {
        if (!isPhotoOrVideo(file) || file.size > MAX_FILE_BYTES || batch.length >= MAX_FILES) {
          refused.push(file.name)
          continue
        }
        const kind = contentTypeOf(file).startsWith('video/') ? 'video' : 'photo'
        batch.push({
          key: `u${++seq}`,
          name: file.name,
          kind,
          preview: kind === 'photo' && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : undefined,
          target,
          targetName,
          step: 'queued',
          progress: 0,
          file,
        })
      }
      if (batch.length) {
        setUploads((list) => [...batch, ...list])
        void run(batch)
      }
      return refused
    },
    [run],
  )

  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  /** OnlyFans has these: let the page show the real tiles, then drop the placeholders. */
  const land = useCallback(async (keys: string[]) => {
    try {
      await landed.current(keys)
    } finally {
      if (mounted.current) {
        for (const u of live.current) if (keys.includes(u.key) && u.preview) URL.revokeObjectURL(u.preview)
        setUploads((list) => list.filter((u) => !keys.includes(u.key)))
      }
    }
  }, [])

  // Follow the running jobs.
  const jobIds = [...new Set(uploads.filter((u) => u.jobId && u.step !== 'done' && u.step !== 'failed').map((u) => u.jobId as string))]
  const watching = jobIds.join(',')
  useEffect(() => {
    if (!api || !watching) return
    let stopped = false
    const tick = async () => {
      const done: string[] = []
      for (const jobId of watching.split(',')) {
        let job: JobDetail
        try {
          job = await api.job(jobId)
        } catch {
          continue // next tick
        }
        if (stopped) return
        const waiting = job.account_rows.some((a) => a.status === 'waiting')
        const accountError = job.account_rows.find((a) => a.error)?.error ?? undefined
        const byFile = new Map(job.items_grid.map((i) => [i.file_id, i]))
        const changes = new Map<string, Partial<VaultUpload>>()
        for (const u of live.current) {
          if (u.jobId !== jobId || u.step === 'done' || u.step === 'failed') continue
          const item = u.fileId ? byFile.get(u.fileId) : undefined
          if (!item) continue
          if (item.status === 'done') {
            done.push(u.key)
            changes.set(u.key, { step: 'done' })
          } else if (item.status === 'failed' || item.status === 'cancelled' || job.status !== 'running') {
            changes.set(u.key, { step: 'failed', error: item.error || accountError || u.error })
          } else {
            changes.set(u.key, { step: waiting ? 'waiting' : item.status, error: undefined })
          }
        }
        setUploads((list) => list.map((u) => (changes.has(u.key) ? { ...u, ...changes.get(u.key) } : u)))
      }
      // Not tied to this poll: marking the last file done ends the poll itself.
      if (!stopped && done.length) void land(done)
    }
    void tick()
    const id = window.setInterval(() => void tick(), POLL_MS)
    return () => {
      stopped = true
      window.clearInterval(id)
    }
  }, [api, watching, land])

  /** Try a failed upload again: re-send it, or retry its job on OnlyFans. */
  const retry = useCallback(
    async (key: string) => {
      const u = live.current.find((x) => x.key === key)
      if (!api || !u || u.step !== 'failed') return
      if (u.jobId) {
        const keys = live.current.filter((x) => x.jobId === u.jobId && x.step === 'failed').map((x) => x.key)
        patch(keys, { step: 'pending', error: undefined })
        try {
          await api.retry(u.jobId)
        } catch (e) {
          patch(keys, { step: 'failed', error: e instanceof Error ? e.message : String(e) })
        }
      } else {
        await run([u])
      }
    },
    [api, patch, run],
  )

  const dismiss = useCallback((key: string) => {
    const u = live.current.find((x) => x.key === key)
    if (u?.preview) URL.revokeObjectURL(u.preview)
    setUploads((list) => list.filter((x) => x.key !== key))
  }, [])

  useEffect(
    () => () => {
      for (const c of aborts.current) c.abort()
    },
    [],
  )

  const sending = uploads.some((u) => u.step === 'queued' || u.step === 'staging')
  return { uploads, add, retry, dismiss, sending }
}
